<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { MonospaceWorkspace } from '#layers/data-model/public';
import type { SettingsSection } from '#layers/global-settings/public';
import { storeToRefs } from 'pinia';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { navigateTo } from '#app/composables/router';
import { canCreateWorkspace, canEditOrganizationSettings, canReadOrganizationMembers, useMemberWorkspaces, useOrganizationEntitlement, useSessionEnd } from '#layers/access/public';
import { BrandLogo, WorkspaceLogo } from '#layers/assets/public';
import { useOrgSummary, useSettingsOverlay } from '#layers/global-settings/public';
import { WORKSPACE_HOME_ROUTE_NAME } from '#layers/spaces/public';
import { selectSwitcherWorkspaces } from '#layers/ui-workspace/utils/select-switcher-workspaces';
import { BreadcrumbSwitcherButton, useFoldedSwitcherPopover, useTruncationTooltip } from '#layers/ui/public';
import { useWorkspaceDisplayNames, useWorkspaceStore } from '#layers/workspace/public';

type SwitcherItem = DropdownMenuItem & { workspace?: MonospaceWorkspace };

interface Props {
	placement: 'breadcrumb' | 'sidebar';
	folded?: boolean;
}

const { placement, folded = false } = defineProps<Props>();

const { t } = useI18n();
const { currentWorkspace } = storeToRefs(useWorkspaceStore());
const { memberWorkspaces } = useMemberWorkspaces();
const { can: canOrganization } = useOrganizationEntitlement();
const { getWorkspaceDisplayName } = useWorkspaceDisplayNames();
const { open } = useSettingsOverlay();
const { logout } = useSessionEnd();
const { orgSettings, orgName, memberCount } = useOrgSummary();
const canReadMembers = computed(() => canOrganization(canReadOrganizationMembers));
const canEditOrgSettings = computed(() => canOrganization(canEditOrganizationSettings));
const canCreate = computed(() => canOrganization(canCreateWorkspace));
const currentWorkspaceName = computed(() =>
	currentWorkspace.value ? getWorkspaceDisplayName(currentWorkspace.value) : '',
);
const { menuContent } = useFoldedMenu();
const nameTooltip = useTruncationTooltip(useTemplateRef('name'));

// The header buttons live inside the menu content, so close the menu explicitly before the
// overlay opens on top of the current route (menu items would auto-close, a raw button won't).
const menuOpen = ref(false);

function openOverlay(section: SettingsSection) {
	menuOpen.value = false;
	open(section);
}

const switcherSelection = computed(() => selectSwitcherWorkspaces({
	current: currentWorkspace.value,
	members: memberWorkspaces.value,
	getName: getWorkspaceDisplayName,
}));

// Shared class for the footer menu rows (View All / Create Workspace / Log Out).
const footerItemClass = 'items-center px-2 py-1.5 text-sm font-normal';

const items = computed<SwitcherItem[][]>(() => {
	const groups: SwitcherItem[][] = [
		[
			{ label: orgName.value, slot: 'org', type: 'label', class: 'p-2' },
		],
		switcherSelection.value.workspaces.map(({ workspace, isCurrent }) => ({
			label: getWorkspaceDisplayName(workspace),
			workspace,
			type: 'checkbox',
			checked: isCurrent,
			onSelect: isCurrent ? undefined : () => navigateTo({ name: WORKSPACE_HOME_ROUTE_NAME, params: { workspace: workspace.apiName } }),
			class: 'items-center gap-1.5 py-1.5 ps-1.5 pe-2 text-sm font-normal',
		})),
	];

	// "View All" (only when the cap hides workspaces) and "Create Workspace" share the footer group.
	const footer: SwitcherItem[] = [];

	if (switcherSelection.value.hasMore) {
		footer.push({ label: t('workspace.switcher.view_all_workspaces'), slot: 'view-all', onSelect: () => open('organization/workspaces'), class: footerItemClass });
	}

	if (canCreate.value) {
		footer.push({ label: t('workspace.switcher.create_workspace'), to: { name: 'workspaces.create' }, class: footerItemClass });
	}

	if (footer.length > 0) groups.push(footer);
	groups.push([{ label: t('account.logout'), slot: 'logout', onSelect: () => logout(), class: footerItemClass }]);

	return groups;
});

function useFoldedMenu() {
	// Folded into the `…` panel: open to the side like a nested menu (shared position with the popover selectors). The modal menu keeps the panel open itself, so it needs the position but no dismissal guards.
	const { foldedPosition } = useFoldedSwitcherPopover();

	const menuContent = computed(() => folded
		? foldedPosition.value
		: { side: 'bottom' as const, align: 'start' as const });

	return { menuContent };
}
</script>

<template>
	<DDropdownMenu
		v-model:open="menuOpen"
		:items
		:content="menuContent"
		:ui="{
			content: 'max-w-80 min-w-55 p-0',
			group: 'mx-0 gap-0.5 p-2 first:p-0 last:pb-2',
			item: 'mx-0',
			label: 'mx-0 w-full',
		}"
	>
		<template #default>
			<BreadcrumbSwitcherButton
				v-if="folded"
				folded
				:label="currentWorkspaceName"
				:pressed="menuOpen"
			/>

			<DTooltip v-else v-bind="nameTooltip" :text="currentWorkspaceName">
				<DButton
					variant="ghost"
					color="neutral"
					data-testid="workspace-switcher-trigger"
					:trailing-icon="placement === 'sidebar' ? 'lucide:chevron-down' : undefined"
					:pressed="menuOpen"
					:aria-label="$t('workspace.switcher.aria_label')"
					:ui="{
						base: `group min-w-0 gap-1 ${placement === 'sidebar' ? 'ps-2' : 'block-7'}`,
						trailingIcon: 'size-4 shrink-0 text-toned transition-transform duration-200 group-aria-expanded:rotate-180',
					}"
				>
					<span ref="name" class="min-w-0 truncate text-sm font-semibold">{{ currentWorkspaceName }}</span>
				</DButton>
			</DTooltip>
		</template>

		<template #org-label>
			<!-- ps-1.5 aligns the org logo with the workspace rows below (their group has p-2 while this
				first group is p-0); pt-1.5 adds breathing room above the logo at the top of the menu. -->
			<div class="flex w-full flex-col gap-2.5 ps-1.5 pt-1.5">
				<div class="flex items-center gap-2.5">
					<BrandLogo :logo-id="orgSettings?.logoId" :color="orgSettings?.color" size="md" class="shrink-0" />
					<div class="flex min-w-0 flex-col">
						<DTextOverflow data-testid="workspace-switcher-org" class="text-sm font-semibold" :text="orgName" />
						<span v-if="memberCount !== undefined" class="truncate text-xs font-normal text-muted">{{ $t('common.members_count', { n: memberCount }) }}</span>
					</div>
				</div>
				<div v-if="canEditOrgSettings || canReadMembers" class="flex gap-2">
					<DButton
						v-if="canEditOrgSettings"
						variant="outline"
						color="neutral"
						size="xs"
						icon="lucide:settings"
						:label="$t('common.settings')"
						block
						@click="openOverlay('organization/general')"
					/>
					<DButton
						v-if="canReadMembers"
						variant="outline"
						color="neutral"
						size="xs"
						icon="lucide:users"
						:label="$t('common.members')"
						data-testid="workspace-switcher-members"
						block
						@click="openOverlay('organization/members')"
					/>
				</div>
			</div>
		</template>

		<template #view-all-label>
			<span data-testid="workspace-switcher-view-all">{{ $t('workspace.switcher.view_all_workspaces') }}</span>
		</template>

		<template #logout-label>
			<span data-testid="workspace-switcher-logout">{{ $t('account.logout') }}</span>
		</template>

		<template #item-leading="{ item }">
			<WorkspaceLogo
				v-if="item.workspace"
				:workspace="item.workspace"
				size="xs"
			/>
		</template>
	</DDropdownMenu>
</template>
